import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
  ReactNode,
  useMemo,
  useCallback,
} from "react";

import * as authHelper from "../helpers/AuthHelpers";
import { getCompanyDetails } from "services/authRequests";
import {
  GetCompanyDetails,
  Preference,
  UserDetails,
} from "helpers/models/dashboard";
import { getUser, getUserPreferences } from "services/userManagementAPIs";
import { useAuth0 } from "@auth0/auth0-react";
import { ERROR_STATUS_KEY } from "constant/errorCode";
import { setCookie } from "helpers/common/genericFunction";
import { initPreferenceValue } from "constant/init-values/initialValue";
import { updateColors } from "components/theme/ThemeHelpers";
import { useThemeMode } from "./theme";
import apiInterceptor from "services/apiInterceptor";
import {authApiEndpoint, subscriptionApiEndpoint} from "constant/apiConstants";
import { jwtDecode } from "jwt-decode";
import {
  getRegionBasedBackendUrl,
  getRegionWiseRedirectUrl,
  setRegion,
  setRegionBasedOnDomain,
} from "helpers/utils";
import { authorizationParams } from "constant/commonConstant";
import sseLogout from "../utils/SSELogoutUtility";

type WithChildren = {
  children?: ReactNode;
};
type AuthContextProps = {
  currentCompany: GetCompanyDetails;
  setCurrentCompany: Dispatch<SetStateAction<GetCompanyDetails>>;
  systemLogout: () => void;
  companyDetailFormDashboarding: () => void;
  fetchUserDetail: () => void;
  fetchSubcriptionDetail: () => void;
  fetchUserPreferencesHandler: () => void;
  userDetails: any;
  featureLimitStatus: any;
  isAuthenticatedSubscription: boolean;
  setIsAuthenticatedSubscription: Dispatch<SetStateAction<boolean>>;
  userPreference: Preference;
  setUserPreference: Dispatch<SetStateAction<Preference>>;
  companySubscriptionDetails: any;
  companyProductSubscriptionDetails:any;
  subscriptionFetchError: boolean;
  isReadOnlyFeature: any;
  isLoadingPrivileges: boolean;
  setIsLoadingPrivileges: any;
  fetchSubscriptionPlanConfiguration: () => void;
  subscriptionPlanConfiguration: any;
  sseConnected: boolean;
  sseError: string | null;
  connectSSE: () => void;
  disconnectSSE: () => void;
  companyDetailsLoading:boolean
};

const initCompanyDetatils = {
  address1: "",
  address2: "",
  cityGuid: "",
  cityName: "",
  companyDetailsGuid: "",
  companyDomainGuid: "",
  companyDomainName: "",
  contactNumber: "",
  countryGuid: "",
  countryName: "",
  email: "",
  isReseller: "",
  name: "",
  pinCode: "",
  stateGuid: "",
  stateName: "",
  websiteUrl: "",
  isTrialAdded: "",
};

const initAuthContextPropsState = {
  currentCompany: initCompanyDetatils,
  setCurrentCompany: () => {},
  systemLogout: () => {},
  companyDetailFormDashboarding: () => {},
  fetchUserDetail: () => {},
  fetchUserPreferencesHandler: () => {},
  userDetails: undefined,
  isAuthenticatedSubscription: false,
  userPreference: initPreferenceValue,
  setIsAuthenticatedSubscription: () => {},
  setUserPreference: () => {},
  fetchSubcriptionDetail: () => {},
  companySubscriptionDetails: undefined,
  companyProductSubscriptionDetails:undefined,
  subscriptionFetchError: false,
  isReadOnlyFeature: () => {},
  featureLimitStatus: {},
  isLoadingPrivileges: true,
  setIsLoadingPrivileges: () => {},
  fetchSubscriptionPlanConfiguration: () => {},
  subscriptionPlanConfiguration: undefined,
  sseConnected: false,
  sseError: null,
  connectSSE: () => {},
  disconnectSSE: () => {},
  companyDetailsLoading:false
};

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState);

const useAuth = () => {
  return useContext(AuthContext);
};

const AuthProvider: FC<WithChildren> = ({ children }) => {
  const [currentCompany, setCurrentCompany] =
    useState<GetCompanyDetails>(initCompanyDetatils);
  const [userDetails, setUserDetails] = useState<UserDetails | undefined>();
  const [isAuthenticatedSubscription, setIsAuthenticatedSubscription] =
    useState(false);
const [companyDetailsLoading,setCompanyDetailsLoading]=useState(false)
  const [isLoadingPrivileges, setIsLoadingPrivileges] = useState(true);
  const [companySubscriptionDetails, setCompanySubscriptionDetails] =
    useState<any>();
    const [companyProductSubscriptionDetails, setcompanyProductSubscriptionDetails] =
    useState<any>();
  const [subscriptionFetchError, setSubscriptionFetchError] = useState(false);
  const [subscriptionPlanConfiguration, setSubscriptionPlanConfiguration] =
    useState<any>();
  const [featureLimitStatus, setFeatureLimitStatus] = useState<any>({});
  const { logout, isAuthenticated, getIdTokenClaims } = useAuth0();

  // SSE logout state
  const [sseConnected, setSseConnected] = useState(false);
  const [sseError, setSseError] = useState<string | null>(null);

  const systemLogout = useCallback(async () => {
    sseLogout.disconnect();

    try {
      const token = await getTokenFromClaims();

      if (token) {
        await fetch(`${getRegionBasedBackendUrl() || ''}${authApiEndpoint.LOGOUT}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            [process.env.REACT_APP_CUSTOM_HEADER_KEY || 'x_one_user_agt']: process.env.REACT_APP_CUSTOM_HEADER_VALUE || '',
            'Content-Type': 'application/json'
          },
        });
        console.log('Server logout successful');
      } else {
        console.warn('No token available for logout request');
      }
    } catch (error) {
      console.error('Server logout error:', error);
    } finally {
      // Clean up auth data and perform Auth0 logout regardless of server response
      authHelper.removeAuth();
      logout({
        logoutParams: {
          federated: true,
          returnTo: window.location.origin,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logout, isAuthenticated, userDetails]);

  const getTokenFromClaims = async () => {
    const idToken = await getIdTokenClaims();
    return idToken?.__raw || null;
  };

  // SSE logout implementation configured for Bizro
  const connectSSE = async () => {
    try {
      let url = getRegionBasedBackendUrl() + authApiEndpoint.EVENT_STREAM;
      await sseLogout.connect({
        url: url,
        queryParams: {product: 'Bizro'},
        customHeaders: {
          [process.env.REACT_APP_CUSTOM_HEADER_KEY || 'x_one_user_agt']: process.env.REACT_APP_CUSTOM_HEADER_VALUE || ''
        },
        getToken: getTokenFromClaims,
        onLogout: systemLogout,
        debug: process.env.NODE_ENV === 'development'
      });
      setSseConnected(true);
      setSseError(null);
    } catch (error) {
      setSseConnected(false);
      setSseError('Failed to connect to SSE');
      console.error('[Auth] SSE connection failed:', error);
    }
  };

  const disconnectSSE = () => {
    sseLogout.disconnect();
    setSseConnected(false);
    setSseError(null);
  };

  const companyDetailFormDashboarding = async () => {
    setCompanyDetailsLoading(true)
    try {
      await getCompanyDetails()
        .then((res: any) => {
          if (res.data.status === ERROR_STATUS_KEY) {
            systemLogout();
          } else {
            setCompanyDetailsLoading(false)
            setCurrentCompany(res.data.data);
          }
        })
        .catch((error) => {
          console.log(error);
          setCompanyDetailsLoading(false)
        });
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  const fetchUserDetail = async () => {
    try {
      await getUser()
        .then((response: any) => {
          setUserDetails(response.data.data);
          setIsAuthenticatedSubscription(true);
        })
        .catch((error) => {
          console.log(error);
        });
    } catch (error) {
      console.error(error);
    }
  };

  // ----useStates----
  const { updateMode, updateMenuMode } = useThemeMode();
  const [userPreference, setUserPreference] =
    useState<Preference>(initPreferenceValue);

  const fetchUserPreferencesHandler = async () => {
    try {
      await getUserPreferences()
        .then((response: any) => {
          setUserPreference(response.data.data);
          if (response.data.data.themeColor) {
            if (response.data.data.themeColor.charAt(0) === "#") {
              updateColors(response.data.data.themeColor);
            } else {
              updateColors("#" + response.data.data.themeColor);
            }
          }
          updateMenuMode(response.data.data.systemMode.toLowerCase());
          updateMode(response.data.data.systemMode.toLowerCase());
        })
        .catch((error) => {
          console.log(error);
        });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSubcriptionDetail = async () => {
    try {
      await apiInterceptor
        .get(subscriptionApiEndpoint.CURRENT_COMPANY_SUBSCRIPTION)
        .then((response: any) => {
          if (response.data.status === "ERROR") {
            setSubscriptionFetchError(true);
            return;
          } else {
            setSubscriptionFetchError(false);
            if (response.data.data.length > 0) {
              const augmentedSubscriptions = response.data.data.map(
                (subscription: any) => ({
                  ...subscription,
                  isExpired: subscription?.isExpired === "Y",
                  havePlan: true,
                })
              );

              setcompanyProductSubscriptionDetails(augmentedSubscriptions);

              const hireSubscription = augmentedSubscriptions.find(
                (subscription: any) =>
                  subscription?.subscriptionPricingPlan?.subscriptionPlanProduct
                    ?.productType?.name === "HIRE"
              );
              setCompanySubscriptionDetails(
                hireSubscription ?? augmentedSubscriptions[0]
              );
            } else {
              setCompanySubscriptionDetails({
                isExpired: true,
                havePlan: false,
              });
            }

            const isExceedJson: any = {};
            response.data.data.forEach((subscription: any) => {
              (subscription.companySubscriptionUsage ?? []).forEach((usage: any) => {
                const featureKey: any = `IS_${usage.features.featuresName}_EXCEED`;
                isExceedJson[featureKey] =
                  isExceedJson[featureKey] || usage.totalAvailableCount === 0;
              });
            });
            setFeatureLimitStatus(isExceedJson);
          }
        })
        .catch((error) => {
          console.log(error);
          setSubscriptionFetchError(true);
        });
    } catch (error) {
      console.error(error);
      setSubscriptionFetchError(true);
    }
  };

  const fetchSubscriptionPlanConfiguration = async () => {
    try {
      await apiInterceptor
        .get(
          `${subscriptionApiEndpoint.ENDPOINT_SUBSCRIPTIONS}/plan-configurations`
        )
        .then((response: any) => {
          if (response.data.status === "ERROR") {
            return;
          } else {
            setSubscriptionPlanConfiguration(response.data.data);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    } catch (error) {
      console.error(error);
    }
  };

  const isReadOnlyFeature = (isExceedFeature = false): boolean => {
    if (!companySubscriptionDetails) {
      return true;
    }
    if (companySubscriptionDetails?.isExpired) {
      return true;
    }
    return !userDetails?.isSystemAdmin || isExceedFeature;
  };

  const contextValue = useMemo(
    () => ({
      systemLogout,
      currentCompany,
      featureLimitStatus,
      setCurrentCompany,
      companyDetailFormDashboarding,
      fetchUserDetail,
      fetchUserPreferencesHandler,
      userDetails,
      userPreference,
      setUserPreference,
      isReadOnlyFeature,
      isAuthenticatedSubscription,
      setIsAuthenticatedSubscription,
      companySubscriptionDetails,
      fetchSubcriptionDetail,
      isLoadingPrivileges,
      setIsLoadingPrivileges,
      fetchSubscriptionPlanConfiguration,
      subscriptionPlanConfiguration,
      // SSE logout related
      sseConnected,
      sseError,
      connectSSE,
      disconnectSSE,
      companyDetailsLoading,
      companyProductSubscriptionDetails,
      subscriptionFetchError
    }),
    // eslint-disable-next-line
    [
      currentCompany,
      setCurrentCompany,
      userDetails,
      userPreference,
      companySubscriptionDetails,
      setUserPreference,
      setIsAuthenticatedSubscription,
      isLoadingPrivileges,
      setIsLoadingPrivileges,
      systemLogout,
      subscriptionFetchError,
      subscriptionPlanConfiguration,
      sseConnected,
      sseError,
      companyDetailsLoading,
      companyProductSubscriptionDetails
    ]
  );
  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

const AuthInit: FC<WithChildren> = ({ children }) => {
  const {
    fetchUserDetail,
    fetchUserPreferencesHandler,
    companyDetailFormDashboarding,
    systemLogout,
    fetchSubscriptionPlanConfiguration,
    fetchSubcriptionDetail,
    setIsLoadingPrivileges,
    connectSSE,
  } = useAuth();
  const {
    isAuthenticated,
    getIdTokenClaims,
    getAccessTokenSilently,
    loginWithRedirect,
  } = useAuth0();
  const didRequest = useRef(false);
  const [isStoredToken, setIsStoredToken] = useState(false);
  useEffect(() => {
    const requestUser = async (apiToken: string) => {
      try {
        if (!didRequest.current) {
          if (apiToken) {
            await Promise.all([
              companyDetailFormDashboarding(),
              fetchUserDetail(),
              fetchUserPreferencesHandler(),
              fetchSubcriptionDetail(),
            fetchSubscriptionPlanConfiguration()
            ]);
            // Connect SSE after successful authentication and data fetching
            console.log('User data fetched successfully, connecting SSE for Bizro...');
            connectSSE();
          } else {
            console.warn('No API token available for user request');
          }
        }
      } catch (error) {
        console.error(error);
        if (!didRequest.current) {
          systemLogout();
        }
      }
      return () => (didRequest.current = true);
    };

    const hash = window.location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.replace("#", ""));
      const token = params.get("access_token");
      if (token) {
        authHelper.setAuthAccessToken(token);
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }
    }
    if (authHelper.getSubscriptionAccessToken().length > 0 && isStoredToken) {
      requestUser(authHelper.getSubscriptionAccessToken());
    }
    // eslint-disable-next-line
  }, [isStoredToken]);

  useEffect(() => {
    (async function () {
      if (isAuthenticated) {
        try {
          setIsLoadingPrivileges(true);
          let accessToken = await getAccessTokenSilently({
            cacheMode: "off",
            authorizationParams: authorizationParams,
          });
          setIsLoadingPrivileges(false);
          const claims = await getIdTokenClaims();
          const decodedToken = jwtDecode<any>(accessToken);
          setRegion(decodedToken?.region);
          getRegionWiseRedirectUrl(decodedToken?.region);
          authHelper.setSubscriptionAccessToken(accessToken);
          const cvalue: string = claims?.["org_id"] ?? "";
          setCookie(authHelper.ORG_KEY, cvalue);
          setIsStoredToken(true);
        } catch {
          loginWithRedirect();
          setIsLoadingPrivileges(false);
        }
      } else {
        setRegionBasedOnDomain();
      }
    })(); // eslint-disable-next-line
  }, [
    isAuthenticated,
    getIdTokenClaims,
    getAccessTokenSilently,
    loginWithRedirect,
  ]);

  return <>{children}</>;
};

export { AuthProvider, AuthInit, useAuth };
